import * as React from 'react';
import Box from '@mui/material/Box';

import isPointerInside from 'shared/helpers/isPointerInside';
import { useEvent, useHoverDirty } from 'react-use';

const ResponsiveDrawer: React.FC<Props> = React.forwardRef(
    (props, ref): React.ReactElement => {
        const {
            Footer,
            children,
            onClose,
            onZoomChanged,
            isDocumentSuccess,
            documentMessage,
            documentPlaceholder,
            documentReason,
            innerRef,
            documentRef,
            imageContentRef,
            isFullScreen,
            setFullScreen,
        } = props;
        const footerRef = React.useRef<Element>(null);
        const isHovering = useHoverDirty(footerRef);
        const innerContentWrapperRef = React.useRef<HTMLElement | null>(null);
        const [scrollBarHeight, setScrollBarHeight] = React.useState<number>(0);
        const [isZoomChanged, setZoomChanged] = React.useState<boolean>(false);

        useEvent('resize', () => {
            const { current: contentWrapper } = innerContentWrapperRef;
            if (contentWrapper) {
                const scrollbarHeight =
                    contentWrapper.offsetHeight - contentWrapper.clientHeight;
                setScrollBarHeight(scrollbarHeight);
            }
        });
        React.useLayoutEffect(() => {
            if (isZoomChanged) {
                const { current: contentWrapper } = innerContentWrapperRef;
                if (contentWrapper) {
                    const scrollbarHeight =
                        contentWrapper.offsetHeight -
                        contentWrapper.clientHeight;
                    setScrollBarHeight(scrollbarHeight);
                }

                setZoomChanged(false);
            }
        }, [isZoomChanged]);
        const handleZoomChanged = React.useCallback(
            (val: number) => {
                setZoomChanged(true);
                if (typeof onZoomChanged === 'function') {
                    onZoomChanged(val);
                }
            },
            [onZoomChanged]
        );
        const footerContainerSX = React.useMemo(() => {
            if (typeof isFullScreen === 'boolean') {
                return {
                    bottom: `${scrollBarHeight}px !important`,
                    width: isFullScreen ? '270px' : 'auto    ',
                    alignSelf: isFullScreen ? 'end' : 'initial',
                };
            }
            return {
                bottom: `${scrollBarHeight}px !important`,
            };
        }, [isFullScreen, scrollBarHeight]);

        const mainContainerSX = React.useMemo(() => {
            if (typeof isFullScreen === 'boolean') {
                return {
                    backgroundColor: isFullScreen
                        ? '#F6F6F6 !important'
                        : 'transparent',
                };
            }
            return {
                backgroundColor: '#F6F6F6 !important',
            };
        }, [isFullScreen]);
        React.useImperativeHandle(
            ref,
            () => ({
                /**
                 * Check pointer is on footer area
                 * @param {MouseEvent} event - event object
                 * @return {boolean} condition to check mouse position inside footer container.
                 */
                isPointerOnFooter: (event: MouseEvent) => {
                    const { current: element } = footerRef;

                    const { current: contentWrapper } = innerContentWrapperRef;
                    if (contentWrapper) {
                        if (
                            event.y > contentWrapper.clientHeight &&
                            event.y < contentWrapper.offsetHeight
                        ) {
                            return true;
                        }
                    }
                    if (element) return isPointerInside(element, event);
                    return isHovering;
                },
            }),
            [isHovering]
        );
        return (
            <Box className="footer-wrapper fixpacket-container">
                <Box
                    ref={innerContentWrapperRef}
                    component="main"
                    aria-label="container with footing menu"
                    className="main-content"
                    sx={mainContainerSX}
                >
                    {children}
                </Box>
                <Box
                    component="nav"
                    className="footer-container"
                    ref={footerRef}
                    sx={footerContainerSX}
                >
                    <Footer
                        ref={innerRef}
                        imageContentRef={imageContentRef}
                        documentRef={documentRef}
                        onClose={onClose}
                        onZoomChanged={handleZoomChanged}
                        documentMessage={documentMessage}
                        documentPlaceholder={documentPlaceholder ?? ''}
                        documentReason={documentReason ?? ''}
                        isDocumentSuccess={isDocumentSuccess}
                        isFullScreen={isFullScreen}
                        setFullScreen={setFullScreen}
                    />
                </Box>
            </Box>
        );
    }
);

export default ResponsiveDrawer;
